import { html, TemplateResult, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import encapsulatedStyles from './styles/outline-social-share.inner.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import '../outline-svg/outline-svg';
export const socialShareOrientations = ['horizontal', 'vertical'] as const;
export type SocialShareOrientation = (typeof socialShareOrientations)[number];
export const socialShareThemes = ['light', 'dark'] as const;
export type SocialShareTheme = (typeof socialShareThemes)[number];

export type SocialLinkItem = {
  icon: string;
  display: string;
  share: string | boolean;
};

export type SocialLinkOptions = {
  insta: SocialLinkItem;
  li: SocialLinkItem;
  fb: SocialLinkItem;
  tw: SocialLinkItem;
  yt: SocialLinkItem;
  vi: SocialLinkItem;
  email: SocialLinkItem;
};

/**
 * The Outline Social Share component provides social media sharing and linking functionality.
 *
 * Note: While functional, this component does not follow recommended implementation patterns.
 * The current implementation handles all logic client-side and renders components within
 * shadow DOM, which can impact performance, analytics, and maintainability. A server-side implementation would be
 * preferred.
 *
 * @element outline-social-share
 * @extends LitElement
 *
 * @property {string|boolean} insta - Instagram profile URL or boolean to enable sharing
 * @property {string|boolean} fb - Facebook profile URL or boolean to enable sharing
 * @property {string|boolean} tw - Twitter/X profile URL or boolean to enable sharing
 * @property {string|boolean} li - LinkedIn profile URL or boolean to enable sharing
 * @property {string|boolean} yt - YouTube profile URL or boolean to enable sharing
 * @property {string|boolean} vi - Vimeo profile URL or boolean to enable sharing
 * @property {string|boolean} email - Email mailto link or boolean to enable email sharing
 * @property {string} size - Sets height/width of icons in any valid CSS spacing value
 * @property {SocialShareTheme} theme - Sets icon color scheme ('light' or 'dark')
 * @property {SocialShareOrientation} orientation - Sets layout direction ('horizontal' or 'vertical')
 * @property {boolean} sharing - Enables social media sharing functionality
 * @property {boolean} display - Shows platform names next to icons when true
 * @property {string} shareLink - URL to share (defaults to current page)
 * @property {string} mailTo - Custom mailto link structure for email sharing
 */

@customElement('outline-social-share')
export class OutlineSocialShare extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    encapsulatedCSS: encapsulatedStyles,
  });

  /**
   * Instagram
   */
  @property({ type: String })
  insta: string | boolean;

  /**
   * Facebook
   */
  @property({ type: String })
  fb: string | boolean;

  /**
   * Twitter
   */
  @property({ type: String })
  tw: string | boolean;

  /**
   * LinkedIn
   */
  @property({ type: String })
  li: string | boolean;

  /**
   * YouTube
   */
  @property({ type: String })
  yt: string | boolean;

  /**
   * Vimeo
   */
  @property({ type: String })
  vi: string | boolean;

  /**
   * Email mailto href link
   * mailto:<EMAIL ADDRESS>?subject=<SUBJECT LINE>&body=<BODY TEXT>
   */
  @property({ type: String })
  email: string | boolean;

  /**
   * Sets height/width of icons in any valid css spacing value.
   */
  @property({ type: String })
  size = '20px';

  /**
   * Sets icon color to match light or dark background.
   */
  @property({ type: String })
  theme: SocialShareTheme = 'light';

  /**
   * Sets the orientation to 'horizontal' or 'vertical'
   */
  @property({ type: String })
  orientation: SocialShareOrientation = 'horizontal';

  /**
   * Set to generate a link sharing this page on applicable social media.
   * Can override the default (window.location.href) by passing in a specific url in to the shareLink prop.
   */
  @property({ type: Boolean })
  sharing: boolean;

  /**
   * Set to scaling variant which allows the buttons to shrink at mobile.
   */
  @property({ type: Boolean })
  scaling: boolean;

  /**
   * Set to display platform name next to the icon.
   */
  @property({ type: Boolean })
  display: boolean;

  /**
   * Pass in aria label text. Allows multiple instances on the same page w/o causing accessibility issues. 
   */
  @property({ type: String })
  label = 'social media links';

  /**
   * Overrides the default (current url) link to share.
   */
  @property({ type: String, attribute: 'share-link' })
  shareLink = '';

  /**
   * Default mail-to link structure.
   */
  @property({ type: String, attribute: 'mail-to' })
  mailTo =
    `mailto:?subject=I wanted to share this with you&body=Take a look at this website ${this.shareLink}`;

  // Update the shareLink value after first render to make sure any added share-link prop is able to override the default.
  firstUpdated() {
    if (this.shareLink === '') {
      this.shareLink = window.location.href;
    }
  }

  // All available social icons and their associated svg name,
  // display name, and sharing url prefix if applicable.
  // Dictates the order in which the links are displayed.
  _linkIcons: SocialLinkOptions = {
    fb: {
      icon: 'facebook',
      display: 'Facebook',
      share: 'https://www.facebook.com/sharer/sharer.php?u=',
    },
    insta: {
      icon: 'instagram',
      display: 'Instagram',
      share: false,
    },
    li: {
      icon: 'linkedin',
      display: 'LinkedIn',
      share: 'https://www.linkedin.com/sharing/share-offsite/?url=',
    },
    tw: {
      icon: 'twitter',
      display: 'X',
      share: 'https://twitter.com/intent/tweet?url=',
    },
    yt: {
      icon: 'youtube',
      display: 'YouTube',
      share: false,
    },
    email: {
      icon: 'envelope',
      display: 'Email',
      share: this.mailTo,
    },
    vi: {
      icon: 'vimeo',
      display: 'Vimeo',
      share: false,
    },
  };

  render(): TemplateResult {
    return html`
      <nav class="outline-social-share--nav" aria-label="${this.label}">
        <ul class="outline-social-share--list">
          ${Object.keys(this._linkIcons).map(key => {
            if (
              this[key as unknown as keyof OutlineSocialShare] !== undefined
            ) {
              return this.generateListItem(
                key,
                this[key as unknown as keyof OutlineSocialShare] as string
              );
            } else {
              return;
            }
          })}
        </ul>
      </nav>
    `;
  }

  generateFinalizedURL(key: keyof SocialLinkOptions, propVal: string) {
    const linkOption = this._linkIcons[key];

    if (this.sharing) {
      if (!linkOption.share) {
        return false;
      } else {
        return `${linkOption.share}${this.shareLink}`;
      }
    } else {
      if (
        propVal &&
        propVal.length > 1 &&
        propVal !== '' &&
        propVal !== 'true'
      ) {
        return propVal;
      } else {
        return false;
      }
    }
  }

  generateListItem(icon: string, propVal: string) {
    const iconVal = icon as keyof SocialLinkOptions;
    const finalizedUrl = this.generateFinalizedURL(iconVal, propVal);

    if (iconVal && finalizedUrl) {
      return html`
        <li class="outline-social-share--list-item">
          <a
            class="outline-cta outline-cta--only-icon outline-cta--button-secondary outline-cta--small outline-cta--button outline-social-share--link"
            href=${finalizedUrl}
            target="_blank"
            aria-label="${this.sharing
              ? `share this page via ${this._linkIcons[iconVal].display}`
              : `open ${this._linkIcons[iconVal].display} in new window`}"
          >
            <outline-svg
              size=${this.size}
              name="${this._linkIcons[iconVal].icon}"
            >
            </outline-svg>
            ${this.display ? this._linkIcons[iconVal].display : null}
          </a>
        </li>
      `;
    } else {
      return;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-social-share': OutlineSocialShare;
  }
}
